import type { IEcommerceSuccess } from '@experiences/ecommerce';
import { UiDialog } from '@experiences/ui-common';
import { useRouteResolver } from '@experiences/util';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
    Button,
    Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import React, {
    useCallback,
    useEffect,
    useMemo,
} from 'react';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router';

import * as RouteNames from '../../../common/constants/RouteNames';
import { triggerPortalShellRefresh } from '../../../common/hooks/triggerPortalShellRefresh';

const useStyles = makeStyles(theme =>
    createStyles({
        greenIcon: {
            color: theme.palette.semantic.colorSuccessIcon,
            fontSize: '32px',
        },
    }),
);

const EcommerceSuccessComponent: React.FC<{
    location?: { state?: { ecommerceSuccess?: IEcommerceSuccess } };
}> = ({ location }) => {
    const classes = useStyles();
    const history = useHistory();
    const getRoute = useRouteResolver();
    const { formatMessage: translate } = useIntl();

    useEffect(() => {
        triggerPortalShellRefresh();
    }, []);

    const close = useCallback(() => {
        history.push(getRoute(RouteNames.Licensing));
    }, [ getRoute, history ]);

    const ecommerceSuccess = useMemo(() => {
        if (location?.state?.ecommerceSuccess) {
            return location?.state?.ecommerceSuccess;
        }
        return {
            title: translate({ id: 'CLIENT_CONGRATULATIONS' }),
            line1: translate({ id: 'CLIENT_CONGRATS_PRO_SUBSCRIPTION_TEXT' }),
            line2: translate({ id: 'CLIENT_CONGRATS_TEXT_2' }),
            button: 'DISMISS',
        } as IEcommerceSuccess;
    }, [ location?.state?.ecommerceSuccess, translate ]);

    return (
        <UiDialog
            close={close}
            title={ecommerceSuccess?.title}
            icon={<CheckCircleIcon className={classes.greenIcon} />}
            width="426px"
            dataCy="ecommerce-success"
            actions={[
                <Button
                    variant={ecommerceSuccess.button === 'DISMISS' ? 'outlined' : 'contained'}
                    onClick={close}
                    key="close"
                    data-cy="dialog-dismiss-button">
                    {translate({ id: `CLIENT_${ecommerceSuccess.button}` })}
                </Button>,
            ]}
        >
            <Typography
                style={{ marginBottom: '10px' }}
                data-cy="congrats-text">
                {ecommerceSuccess?.line1}
            </Typography>
            <Typography>
                {ecommerceSuccess?.line2}
            </Typography>
        </UiDialog>
    );
};

export default EcommerceSuccessComponent;
